import { Box, ColorOptions, Icon, Spacer, SpacerNew, TextInputNew, TextInputNewProps, TextNew } from '@jfs/ui-new'
import { DateFormatTypes, dateFormatter } from '@jfs/utils'
import * as React from 'react'
import { ReactElement, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { NativeSyntheticEvent, TextInput as RNTextInput, TextInputKeyPressEventData } from 'react-native'
import { CalendarTypes } from '../../comps/Calendar/types'
import CalendarViewModal from '../../comps/CalendarViewModal'
import { DropdownOptimised } from '../../comps/DropdownNew'
import { DropdownTypes } from '../../comps/DropdownNew/types'
import { ModBox } from '../../comps/ModifiedBox/ModifiedBox'
import { Colors } from '../../util/util'
import { styles } from './styles'

interface props extends TextInputNewProps {
  fieldName?: string
  setFieldValue?: any
  value?: any
  error?: string
  allValues?: any
  touched?: boolean
  testID?: string
  disabled?: boolean
}

const DateTimePicker = ({
  setFieldValue,
  fieldName,
  label,
  placeholder,
  value,
  error,
  touched,
  allValues,
  testID,
  rightComponent,
  disabled = false,
}: props) => {
  const dateString = value ? dateFormatter(new Date(value), DateFormatTypes.DD_MM_YYYY) : ''
  const [visible, setVisible] = useState(false)
  const [text, setText] = useState(dateString)
  const [date, setDate] = useState<any>(value)
  const splitTime: any = () => {
    if (value) {
      let hr = new Date(value).getHours()
      let meridian = 'AM'
      if (hr === 0) {
        hr = 12
      }
      if (hr > 12) {
        meridian = 'PM'
        hr = hr - 12
      }
      const hour = hr < 10 ? '0' + hr : hr
      const mins =
        new Date(value)?.getMinutes() < 10 ? '0' + new Date(value)?.getMinutes() : new Date(value)?.getMinutes()
      const secs =
        new Date(value)?.getSeconds() < 10 ? '0' + new Date(value)?.getSeconds() : new Date(value)?.getSeconds()
      return [hour, mins, secs, meridian]
    }
  }
  const mHour = splitTime()?.[0] ?? ''
  const mMins = splitTime()?.[1] ?? ''
  const mSecs = splitTime()?.[2] ?? ''
  const mMeridiem = splitTime()?.[3] ?? 'AM'
  const [hour, setHour] = useState<string>(mHour)
  const [mins, setMins] = useState<string>(mMins)
  const [secs, setSecs] = useState<string>(mSecs)
  const [meridiem, setMeridiem] = useState(mMeridiem)
  const hoursRef = useRef<RNTextInput>(null)
  const ref = useRef<RNTextInput>(null)
  const secsRef = useRef<RNTextInput>(null)
  const errorTimerRef = useRef<any>(0)
  const [isPressed, setPressed] = useState(false)
  const [isFocused, setFocused] = useState(false)
  const [showError, setShowError] = useState(false)

  const isError = !isFocused && touched && !isPressed
  useEffect(() => {
    if (isError) {
      errorTimerRef.current = setTimeout(() => {
        setShowError(true)
      }, 500)
    } else {
      clearTimeout(errorTimerRef.current)
      setShowError(false)
    }
  }, [isError])

  const handleHourChange = (hValue: string) => {
    const regex = /^(0?[1-9]|1?[012])$/
    if (hValue.length >= 0 && hValue.length <= 2) {
      if (regex.test(hValue) || hValue.length === 0) {
        setHour(hValue)
      } else {
        hour ? setHour('12') : setHour('')
      }
    } else {
      ref?.current?.focus()
      const min = Number(hValue)
      if (!isNaN(min)) {
        setMins(hValue.toString().slice(2))
      }
    }
  }

  const handleOnBlur = (val: string | undefined, isHour: boolean) => {
    if (val?.length === 1 && Number(val) <= 9) {
      isHour ? setHour(`0${val}`) : setMins(`0${val}`)
    }
    setFocused(false)
  }

  const handleSecOnBlur = (val: string | undefined) => {
    if (val?.length === 1 && Number(val) <= 9) {
      setSecs(`0${val}`)
    }
    setFocused(false)
  }
  const handleMinsChange = (minValue: string) => {
    if (minValue.length >= 0 && minValue.length <= 2) {
      if (minValue.length === 1 && parseInt(minValue, 10) <= 9) {
        setMins(minValue)
      } else {
        const reg = /[0-5][0-9]/
        if (reg.test(minValue)) {
          setMins(minValue)
        } else {
          if (minValue?.length > 0) {
            setMins('00')
          } else {
            setMins('')
          }
        }
      }
    } else {
      secsRef?.current?.focus()
      const sec = Number(minValue)
      if (!isNaN(sec)) {
        setSecs(minValue.toString().slice(2))
      }
    }
  }

  const handleSecsChange = (secValue: string) => {
    if (secValue.length === 1 && parseInt(secValue, 10) <= 9) {
      setSecs(secValue)
    } else {
      const reg = /[0-5][0-9]/
      if (reg.test(secValue)) {
        setSecs(secValue)
      } else {
        if (secValue?.length > 0) {
          setSecs('00')
        } else {
          setSecs('')
        }
      }
    }
  }

  const onDeleteKeyPress = ({ nativeEvent }: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (nativeEvent?.key === 'Backspace' && mins?.length === 0) {
      hoursRef?.current?.focus()
    }
  }

  const onDeleteKeyPressOfSecs = ({ nativeEvent }: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (nativeEvent?.key === 'Backspace' && secs?.length === 0) {
      ref?.current?.focus()
    }
  }
  useEffect(() => {
    if (setFieldValue && fieldName) {
      const getHour = (mHr: string, mMeridiems: string) => {
        if (mHr?.length === 0) {
          return mHr
        }
        if (mMeridiems === 'PM') {
          const hr = Number(mHr)
          return hr === 12 ? mHr : `${hr + 12}`
        } else {
          const amHour = Number(mHr)
          return amHour === 12 ? '00' : hour
        }
      }
      const hrs = getHour(hour, meridiem)
      const selectedDate =
        date &&
        new Date(
          new Date(date)?.getFullYear(),
          new Date(date)?.getMonth(),
          new Date(date)?.getDate(),
          +hrs,
          +mins ?? 0,
          +secs ?? 0,
          0,
        )?.getTime()
      setFieldValue(fieldName, !!hrs && !!mins && !!secs && !!date ? selectedDate : undefined)
    }
  }, [hour, mins, secs, meridiem, setFieldValue, fieldName, date, value, text, mHour, mMins, mSecs])
  const onSingleDateSelection = (dateObj: any) => {
    setDate(dateObj)
    setVisible(false)
    setText(dateFormatter(dateObj, DateFormatTypes.DD_MM_YYYY))
  }

  const calendarComp = () => {
    return <Icon size={16} color={'BLACK_21'} id={'CALENDAR'} />
  }

  const getDefaultMaxDate = React.useCallback(() => {
    const endDate =
      allValues?.endDate && !(allValues?.endDate instanceof Date) ? new Date(allValues?.endDate) : allValues?.endDate
    return fieldName === 'startDate' ? endDate : undefined
  }, [allValues?.endDate, fieldName])

  const getDefaultMinDate = React.useCallback(() => {
    const startDate =
      allValues?.startDate && !(allValues?.startDate instanceof Date)
        ? new Date(allValues?.startDate)
        : allValues?.startDate
    return fieldName === 'endDate' ? startDate : undefined
  }, [allValues?.startDate, fieldName])

  const renderCalendarModal = (): ReactElement => {
    return (
      <ModBox {...styles().modal} testID="date-picker-modal" underlayColor="TRANSPARENT" show={visible}>
        <ModBox underlayColor={'TRANSPARENT'} {...styles().overlayStyle} align={'center'} justify={'center'}>
          <ModBox {...styles().dropdownStyle}>
            <CalendarViewModal
              onDateSelection={onSingleDateSelection}
              dateRange={''}
              type={CalendarTypes.SINGLE_FUTURE}
              onCancel={() => setVisible(false)}
              defaultMaxDate={getDefaultMaxDate()}
              defaultMinDate={getDefaultMinDate()}
            />
          </ModBox>
        </ModBox>
      </ModBox>
    )
  }

  const renderPortal = (): React.ReactPortal => {
    return ReactDOM.createPortal(renderCalendarModal(), document.body)
  }

  const fontFamilyStyle = {
    fontFamily: 'jiotype-medium',
    placeholderTextColor: Colors.LABEL_GREY as ColorOptions,
    color: Colors.FONT_BLACK as ColorOptions,
  }
  const openCalender = () => {
    if (!disabled) {
      setVisible(true)
    }
  }
  return (
    <>
      <Box flexDirection={'row'} onPress={() => setPressed(true)} underlayColor={'TRANSPARENT'}>
        {renderPortal()}
        <Box f={1}>
          <TextInputNew
            onPressIn={openCalender}
            label={label}
            labelFontFamily={'jiotype-medium'}
            labelColor={Colors.LABEL_GREY as ColorOptions}
            placeholder={placeholder}
            editable={false}
            rightComponent={disabled ? rightComponent : calendarComp()}
            value={text}
            type={'body12'}
            borderColor={Colors.BORDER_LIGHT_BLUE as ColorOptions}
            focusBorderColor={Colors.BORDER_LIGHT_BLUE as ColorOptions}
            labelPaddingBottom={8}
            borderRadius={4}
            fontSize={12}
            selectionColor={'BLUE'}
            paddingHorizontal={8}
            color={Colors.FONT_COLOR as ColorOptions}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            paddingVertical={8}
            testID={`datePicker${testID}`}
            {...fontFamilyStyle}
          />
        </Box>
        <Box
          flexDirection={'row'}
          borderWidth={1}
          borderRadius={4}
          align={'center'}
          marginTop={26}
          f={1}
          justify={'space-between'}
          ph={12}
          bg={'WHITE'}
          marginLeft={16}
          borderColor={Colors.BORDER_LIGHT_BLUE as ColorOptions}>
          <Box flexDirection={'row'} align={'center'}>
            <TextInputNew
              label={''}
              innerRef={hoursRef}
              placeholder={'HH'}
              editable={!disabled}
              borderColor={'TRANSPARENT'}
              showBottomLine={false}
              textWidth={20}
              fontSize={12}
              type={'body12'}
              color={Colors.FONT_COLOR as ColorOptions}
              placeholderTextColor={Colors.FONT_COLOR as ColorOptions}
              focusBorderColor={'TRANSPARENT'}
              value={hour}
              onChange={handleHourChange}
              onFocus={() => setFocused(true)}
              onBlur={() => handleOnBlur(hour, true)}
              testID={`timeHH${testID}`}
              {...fontFamilyStyle}
            />
            <TextNew title={':'} fontSize={12} fontWeight={'500'} />
            <TextInputNew
              innerRef={ref}
              label={''}
              placeholder={'MM'}
              editable={!disabled}
              borderColor={'TRANSPARENT'}
              showBottomLine={false}
              textWidth={22}
              fontSize={12}
              type={'body12'}
              color={Colors.FONT_COLOR as ColorOptions}
              placeholderTextColor={Colors.FONT_COLOR as ColorOptions}
              focusBorderColor={'TRANSPARENT'}
              // maxLength={2}
              value={mins}
              onChange={handleMinsChange}
              onKeyPress={onDeleteKeyPress}
              onBlur={() => handleOnBlur(mins, false)}
              testID={`timeMM${testID}`}
              {...fontFamilyStyle}
            />
            <TextNew title={':'} fontSize={12} fontWeight={'500'} />
            <TextInputNew
              innerRef={secsRef}
              label={''}
              placeholder={'SS'}
              editable={!disabled}
              borderColor={'TRANSPARENT'}
              showBottomLine={false}
              textWidth={22}
              fontSize={12}
              type={'body12'}
              color={Colors.FONT_COLOR as ColorOptions}
              placeholderTextColor={Colors.FONT_COLOR as ColorOptions}
              focusBorderColor={'TRANSPARENT'}
              maxLength={2}
              value={secs}
              onChange={handleSecsChange}
              onKeyPress={onDeleteKeyPressOfSecs}
              onBlur={() => handleSecOnBlur(secs)}
              testID={`timess${testID}`}
              {...fontFamilyStyle}
            />
            <Spacer direction={'horizontal'} size={4} />
            <DropdownOptimised
              placeHolder={meridiem}
              data={[
                { id: '1', value: 'AM' },
                { id: '2', value: 'PM' },
              ]}
              type={DropdownTypes.DEFAULT_WITHOUT_BORDER}
              onChange={(val: any) => setMeridiem(val)}
              disabled={disabled}
              disabledBg={'WHITE'}
              titleColor={Colors.FONT_BLACK as ColorOptions}
              chevronColor={Colors.FONT_BLACK as ColorOptions}
              fontWeight={'500'}
              // buttonWidth={24}
              // dropdownWidth={24}
              testID={`timeAMPM${testID}`}
            />
          </Box>
          <Icon id={disabled ? 'LOCK' : 'TIMER'} size={disabled ? 12 : 16} color={'BLACK_21'} />
        </Box>
      </Box>
      {!!showError && (
        <>
          <SpacerNew size={2} />
          <TextNew color="ERROR" type="body12" fontSize={12} testID={'dateTimePickerError'}>
            {error}
          </TextNew>
        </>
      )}
    </>
  )
}

export { DateTimePicker }
